/**
 * Runtime validation for navigation data structures
 * Ensures compatibility between incoming data and compressor/decompressor
 */

import type { NavItem } from '../types/NavFlyout';
import type { CompressedNavigationData, NavItemRuntime } from '../types/CompressedNav';

export interface ValidationResult {
  valid: boolean;
  errors: string[];
  warnings: string[];
}

/**
 * Validate that a NavItem has the expected structure
 */
function validateNavItem(item: unknown, path: string = 'root'): string[] {
  const errors: string[] = [];

  if (!item || typeof item !== 'object') {
    errors.push(`${path}: NavItem must be an object`);
    return errors;
  }

  // Type guard: now we know item is a non-null object
  const navItem = item as Record<string, unknown>;

  // Required fields
  if (typeof navItem.id !== 'string') {
    errors.push(`${path}.id: must be a string, got ${typeof navItem.id}`);
  }

  if (typeof navItem.text !== 'string') {
    errors.push(`${path}.text: must be a string, got ${typeof navItem.text}`);
  }

  if (navItem.url !== undefined && typeof navItem.url !== 'string') {
    errors.push(`${path}.url: must be a string if present, got ${typeof navItem.url}`);
  }

  // Optional fields with type checks
  if (navItem.analyticsId !== undefined && typeof navItem.analyticsId !== 'string') {
    errors.push(`${path}.analyticsId: must be a string if present`);
  }

  if (navItem.theme !== undefined && typeof navItem.theme !== 'string') {
    errors.push(`${path}.theme: must be a string if present`);
  }

  if (navItem.isVisual !== undefined && typeof navItem.isVisual !== 'boolean') {
    errors.push(`${path}.isVisual: must be a boolean if present`);
  }

  if (navItem.startOfMx !== undefined && typeof navItem.startOfMx !== 'boolean') {
    errors.push(`${path}.startOfMx: must be a boolean if present`);
  }

  if (navItem.pushToNextColumn !== undefined && typeof navItem.pushToNextColumn !== 'boolean') {
    errors.push(`${path}.pushToNextColumn: must be a boolean if present`);
  }

  // Image validation
  if (navItem.image !== undefined && navItem.image !== null) {
    if (typeof navItem.image !== 'object') {
      errors.push(`${path}.image: must be an object if present`);
    } else {
      const image = navItem.image as Record<string, unknown>;
      if (image.url !== undefined && typeof image.url !== 'string') {
        errors.push(`${path}.image.url: must be a string if present`);
      }
    }
  }

  // Children validation
  if (navItem.children !== undefined) {
    if (!Array.isArray(navItem.children)) {
      errors.push(`${path}.children: must be an array if present`);
    } else {
      navItem.children.forEach((child: unknown, idx: number) => {
        // Children can be either string IDs or NavItem objects
        if (typeof child === 'string') {
          // String ID reference - just validate it's a string
          // Full object validation will happen when we process the actual item
          return;
        } else if (child && typeof child === 'object') {
          // Full NavItem object - validate recursively
          const childErrors = validateNavItem(child, `${path}.children[${idx}]`);
          errors.push(...childErrors);
        } else {
          errors.push(`${path}.children[${idx}]: must be a string ID or NavItem object`);
        }
      });
    }
  }

  return errors;
}

/**
 * Validate navigation data before compression
 */
export function validateNavigationForCompression(
  navItems: Record<string, NavItem>,
  topLevelNavNodes: string[]
): ValidationResult {
  const errors: string[] = [];
  const warnings: string[] = [];

  // Check navItems structure
  if (!navItems || typeof navItems !== 'object') {
    errors.push('navItems must be an object/record');
    return { valid: false, errors, warnings };
  }

  const itemCount = Object.keys(navItems).length;
  if (itemCount === 0) {
    warnings.push('navItems is empty');
  }

  if (itemCount > 1000) {
    warnings.push(`Large navigation: ${itemCount} items may cause performance issues`);
  }

  // Validate each item
  Object.entries(navItems).forEach(([key, item]) => {
    if (item.id !== key) {
      errors.push(`Key "${key}" does not match item.id "${item.id}"`);
    }

    const itemErrors = validateNavItem(item, `navItems.${key}`);
    errors.push(...itemErrors);
  });

  // Validate top level nodes
  if (!Array.isArray(topLevelNavNodes)) {
    errors.push('topLevelNavNodes must be an array');
  } else {
    topLevelNavNodes.forEach((nodeId, idx) => {
      if (typeof nodeId !== 'string') {
        errors.push(`topLevelNavNodes[${idx}] must be a string`);
      } else if (!navItems[nodeId]) {
        errors.push(`topLevelNavNodes[${idx}] references non-existent item "${nodeId}"`);
      }
    });
  }

  // Check for circular references (only check children)
  const visited = new Set<string>();
  const checkCircular = (item: NavItem, path: string[]): void => {
    if (visited.has(item.id)) {
      errors.push(`Circular reference detected: ${path.join(' -> ')} -> ${item.id}`);
      return;
    }

    visited.add(item.id);

    if ('children' in item && item.children) {
      item.children.forEach((child) => {
        // Children can be string IDs or objects
        if (typeof child === 'string') {
          // Resolve string ID to actual item
          const childItem = navItems[child];
          if (childItem) {
            checkCircular(childItem, [...path, item.id]);
          }
        } else if (child && typeof child === 'object' && 'id' in child) {
          // Direct object reference
          checkCircular(child as NavItem, [...path, item.id]);
        }
      });
    }

    visited.delete(item.id);
  };

  topLevelNavNodes.forEach((nodeId) => {
    const item = navItems[nodeId];
    if (item) {
      checkCircular(item, []);
    }
  });

  return {
    valid: errors.length === 0,
    errors,
    warnings,
  };
}

/**
 * Validate compressed navigation data
 */
export function validateCompressedNavigation(compressed: unknown): ValidationResult {
  const errors: string[] = [];
  const warnings: string[] = [];

  if (!compressed || typeof compressed !== 'object') {
    errors.push('Compressed data must be an object');
    return { valid: false, errors, warnings };
  }

  // Type guard: now we know compressed is a non-null object
  const data = compressed as Record<string, unknown>;

  // Check version
  if (data.v !== 2) {
    errors.push(`Unsupported compression version: ${data.v}, expected 2`);
  }

  // Check ID map (optional if using numeric IDs mode)
  if (!data.numericIds && !Array.isArray(data.m)) {
    errors.push('Missing or invalid id map (m) - required unless numericIds mode is enabled');
  }

  if (!Array.isArray(data.u)) {
    errors.push('Missing or invalid URL dictionary (u)');
  }

  if (!Array.isArray(data.i)) {
    errors.push('Missing or invalid image URL dictionary (i)');
  }

  if (!Array.isArray(data.s)) {
    errors.push('Missing or invalid string dictionary (s)');
  }

  if (!Array.isArray(data.d)) {
    errors.push('Missing or invalid data array (d)');
  } else {
    // Validate structure of compressed items
    data.d.forEach((item: unknown, idx: number) => {
      if (!Array.isArray(item)) {
        errors.push(`d[${idx}]: must be an array`);
      } else if (item.length < 5) {
        errors.push(`d[${idx}]: array too short, expected at least 5 elements`);
      }
    });
  }

  if (!Array.isArray(data.t)) {
    errors.push('Missing or invalid top level nodes (t)');
  }

  return {
    valid: errors.length === 0,
    errors,
    warnings,
  };
}

/**
 * Create a type guard for compressed navigation data
 */
export function isCompressedNavigationData(data: unknown): data is CompressedNavigationData {
  if (!data || typeof data !== 'object') return false;
  const obj = data as Record<string, unknown>;
  if (obj.v !== 2) return false;
  // ID map is optional if using numeric IDs mode
  if (!obj.numericIds && !Array.isArray(obj.m)) return false;
  if (!Array.isArray(obj.d)) return false;
  if (!Array.isArray(obj.t)) return false;
  return true;
}

/**
 * Safe compression with validation
 */
export function safeCompress(
  compressFunc: () => CompressedNavigationData,
  navItems: Record<string, NavItem>,
  topLevelNavNodes: string[]
): { success: true; data: CompressedNavigationData } | { success: false; error: string } {
  try {
    // Validate input
    const validation = validateNavigationForCompression(navItems, topLevelNavNodes);

    if (!validation.valid) {
      return {
        success: false,
        error: `Validation failed: ${validation.errors.join(', ')}`,
      };
    }

    // Log warnings
    if (validation.warnings.length > 0) {
      console.warn('[NavCompressor] Warnings:', validation.warnings);
    }

    // Perform compression
    const compressed = compressFunc();

    // Validate output
    const outputValidation = validateCompressedNavigation(compressed);
    if (!outputValidation.valid) {
      return {
        success: false,
        error: `Compressed data validation failed: ${outputValidation.errors.join(', ')}`,
      };
    }

    return { success: true, data: compressed };
  } catch (error) {
    return {
      success: false,
      error: `Compression error: ${error instanceof Error ? error.message : String(error)}`,
    };
  }
}

/**
 * Safe decompression with validation
 */
export function safeDecompress(
  decompressFunc: () => {
    navigation: Record<string, NavItemRuntime>;
    topLevelNavNodes: string[];
    topLevelNavNodeTests?: string[];
  },
  compressed: unknown
): {
  success: true;
  data: {
    navigation: Record<string, NavItemRuntime>;
    topLevelNavNodes: string[];
    topLevelNavNodeTests?: string[];
  };
} | { success: false; error: string } {
  try {
    // Validate input
    const validation = validateCompressedNavigation(compressed);

    if (!validation.valid) {
      return {
        success: false,
        error: `Validation failed: ${validation.errors.join(', ')}`,
      };
    }

    // Perform decompression
    const decompressed = decompressFunc();

    // Validate output (basic check)
    if (!decompressed.navigation || typeof decompressed.navigation !== 'object') {
      return {
        success: false,
        error: 'Decompressed navigation is invalid',
      };
    }

    return { success: true, data: decompressed };
  } catch (error) {
    return {
      success: false,
      error: `Decompression error: ${error instanceof Error ? error.message : String(error)}`,
    };
  }
}
